import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Prompt, useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import Typography from '@mui/material/Typography'
import Add from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup, { FORM_IMAGE_CLEAR_BUTTON_SX as IMAGE_CLEAR_BUTTON_SX, FORM_WIDE_UPLOAD_BUTTON_SX } from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX } from '../../component/formSurfaceStyles'
import { buildListSearch as buildListSearchQuery, createInitialListState, createListLinkTarget } from '../../utils/listPageState'
import {
    DONATION_IMAGE_FILE_ACCEPT,
    DONATION_IMAGE_FILE_ERROR_MESSAGE,
    applyDonationDraft,
    clearDonationDraft,
    createDonationDraftPayload,
    createEditDonationDraftKey,
    createLegacyDonationDraftPayload,
    isDonationImageFile,
    readDonationDraft,
    saveDonationDraft,
} from './donationDraftUtils'
import {
    DONATION_IMAGE_LEAVE_MESSAGE,
    hasPendingDonationImages,
    usePendingDonationImageUnloadGuard,
} from './donationLeaveGuard'

const DONATION_SELECT_PARAMS = {
    searchStatus: 'searchStatus',
}
const DONATION_DRAFT_FIELDS = [
    { field: 'title', param: 'draftTitle' },
    { field: 'brandName', param: 'draftBrandName' },
    { field: 'description', param: 'draftDescription' },
    { field: 'point', param: 'draftPoint' },
    { field: 'sortOrder', param: 'draftSortOrder' },
    { field: 'status', param: 'draftStatus' },
]

const getImageUrl = (path) => path ? `${process.env.REACT_APP_AWS_S3_URL}/${path}` : '/images/noimage.jpg'

const IMAGE_PANEL_SX = {
    ...FORM_PANEL_SX,
    height: '100%',
}

const getImageFileName = (imageFile) => {
    if (!imageFile || typeof imageFile !== 'object') {
        return ''
    }

    return imageFile.name || ''
}

// 기부 상품 이미지를 미리보기, 업로드, 선택 취소까지 한 패널 안에서 관리한다.
function DonationImageUploadPanel({
    fieldName,
    label,
    helperText,
    imageSrc,
    imageFile,
    disabled,
    onUpload,
    onClear,
}) {
    const imageFileName = getImageFileName(imageFile)
    const hasPendingFile = imageFile instanceof File
    const fileInputRef = useRef(null)

    const handleClearClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }

        onClear(fieldName)
    }

    return (
        <Paper variant="outlined" sx={IMAGE_PANEL_SX}>
            <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {helperText}
                    </Typography>
                </Box>
                {hasPendingFile && (
                    <Button
                        size="small"
                        color="error"
                        variant="text"
                        startIcon={<RemoveIcon />}
                        onClick={handleClearClick}
                        sx={IMAGE_CLEAR_BUTTON_SX}
                        disabled={disabled}
                    >
                        선택 취소
                    </Button>
                )}
            </Box>
            <Box
                sx={{
                    ...FORM_IMAGE_PREVIEW_FRAME_SX,
                    height: 240,
                    mb: 1.5,
                }}
            >
                <Box
                    component="img"
                    src={imageSrc || '/images/noimage.jpg'}
                    alt={`${label} 미리보기`}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        display: 'block',
                    }}
                />
            </Box>
            <FormControl>
                <TextField
                    inputProps={{ accept: DONATION_IMAGE_FILE_ACCEPT }}
                    inputRef={fileInputRef}
                    style={{ display: "none" }}
                    name={fieldName}
                    id={fieldName}
                    type="file"
                    disabled={disabled}
                    onChange={onUpload(fieldName)}
                />
                <label htmlFor={fieldName}>
                    <Button
                        variant="contained"
                        color="success"
                        component="span"
                        startIcon={<Add />}
                        sx={FORM_WIDE_UPLOAD_BUTTON_SX}
                        disabled={disabled}
                    >
                        이미지 선택
                    </Button>
                </label>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {imageFileName ? `선택한 파일: ${imageFileName}` : '저장 전 미리보기로 이미지가 올바른지 확인하세요.'}
            </Typography>
        </Paper>
    )
}

// 상세 조회 실패 시에도 폼 기본값을 명확하게 되돌릴 수 있게 공통 초기값을 사용한다.
const createDefaultParams = () => ({
    donationProductId: '',
    title: '',
    brandName: '',
    description: '',
    point: '',
    sortOrder: 0,
    status: 1,
    exchangeCount: 0,
    productImage: null,
    thumbnailImage: null,
    detailImage: null,
    letterBaseImage: null,
    regTime: '',
    updTime: '',
})

const createPreviewState = (data = {}) => ({
    productImage: getImageUrl(data.detailImage || data.thumbnailImage),
    letterBaseImage: getImageUrl(data.letterBaseImage),
})

export default function Donation(props) {
    const history = useHistory()
    const location = useLocation()
    const initialPageStateRef = useRef(createInitialListState({
        location,
        defaultSearchSelect: { searchStatus: '' },
        selectParams: DONATION_SELECT_PARAMS,
        getExtraState: ({ locationState, searchParams }) => ({
            draftValues: DONATION_DRAFT_FIELDS.reduce((nextDrafts, { field, param }) => {
                nextDrafts[field] = searchParams.has(param)
                    ? (searchParams.get(param) || '')
                    : (locationState[field] ?? null)
                return nextDrafts
            }, {}),
        }),
    }))
    const initialPageState = initialPageStateRef.current
    const donationProductId = props.match.params.donationProductId
    const draftKey = createEditDonationDraftKey(donationProductId)
    const storedDraftRef = useRef(readDonationDraft(draftKey))
    const legacyDraftRef = useRef(createLegacyDonationDraftPayload(initialPageState.draftValues))
    const listCurPage = initialPageState.curPage
    const listSearchText = initialPageState.searchText
    const listSearchSelect = initialPageState.searchSelect
    const listSearch = buildListSearchQuery({
        curPage: listCurPage,
        searchQuery: listSearchText,
        searchSelect: listSearchSelect,
        selectParams: DONATION_SELECT_PARAMS,
    })
    const [loading, setLoading] = useState(true)
    const [hasDetail, setHasDetail] = useState(false)
    const [saving, setSaving] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [params, setParams] = useState(() => createDefaultParams())
    const [previewImages, setPreviewImages] = useState(createPreviewState())
    const [openSnackbar, setOpenSnackbar] = useState(initialPageState.snackbar.openSnackbar || false)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialPageState.snackbar.severitySnackbar || 'info')
    const [messageSnackbar, setMessageSnackbar] = useState(initialPageState.snackbar.messageSnackbar || '')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const [navigationUnlocked, setNavigationUnlocked] = useState(false)
    const initialDraftSnapshotRef = useRef('')
    const originalImageValuesRef = useRef({ productImage: null, letterBaseImage: null })
    const originalPreviewImagesRef = useRef(createPreviewState())
    const leaveGuardEnabled = hasPendingDonationImages(params) && !saving && !navigationUnlocked

    usePendingDonationImageUnloadGuard(leaveGuardEnabled)

    const resetDetailState = useCallback((errorMessage) => {
        setParams(createDefaultParams())
        setPreviewImages(createPreviewState())
        setHasDetail(false)
        setDraftReady(false)
        setDetailErrorMessage(errorMessage)
    }, [])

    useEffect(() => {
        const getData = async () => {
            if (!donationProductId) {
                const errorMessage = '필수 파라미터가 없어 포인트 기부 상품 상세를 불러올 수 없습니다.'
                resetDetailState(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
                setLoading(false)
                return
            }

            setLoading(true)
            setHasDetail(false)
            setDraftReady(false)
            setDetailErrorMessage('')

            try {
                const { data } = await API.post('/api/v1/admin/donation/getDetail', { donationProductId })

                if (data.code === 200) {
                    if (!data.data?.donationProductId) {
                        resetDetailState('포인트 기부 상품 상세 정보를 찾을 수 없습니다.')
                        return
                    }

                    const baseParams = {
                        ...data.data,
                        productImage: null
                    }
                    const restoredDraft = applyDonationDraft({
                        baseParams,
                        draft: storedDraftRef.current || legacyDraftRef.current,
                    })

                    initialDraftSnapshotRef.current = JSON.stringify(createDonationDraftPayload({
                        params: baseParams,
                    }))

                    const nextPreviewImages = createPreviewState(data.data)

                    originalImageValuesRef.current = {
                        productImage: null,
                        letterBaseImage: baseParams.letterBaseImage,
                    }
                    originalPreviewImagesRef.current = nextPreviewImages
                    setParams(restoredDraft.params)
                    setPreviewImages(nextPreviewImages)
                    setHasDetail(true)
                    setDetailErrorMessage('')
                    setDraftReady(true)
                } else if (data.code === 403) {
                    history.push('/login')
                    return
                } else {
                    const errorMessage = data.message || '포인트 기부 상품 상세를 불러오지 못했습니다.'
                    resetDetailState(errorMessage)
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(errorMessage)
                }
            } catch (_error) {
                const errorMessage = '포인트 기부 상품 상세를 불러오지 못했습니다.'
                resetDetailState(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            } finally {
                setLoading(false)
            }
        }

        getData()
    }, [donationProductId, history, resetDetailState])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    useEffect(() => {
        if (location.search === listSearch) {
            return
        }

        history.replace({
            pathname: location.pathname,
            search: listSearch,
            ...(location.state ? { state: location.state } : {}),
        })
    }, [history, listSearch, location.pathname, location.search, location.state])

    useEffect(() => {
        if (!draftReady || !hasDetail) {
            return
        }

        // 포인트 기부 상품 상세는 목록 query를 건드리지 않고 텍스트 draft만 sessionStorage에 보존한다.
        const nextDraft = createDonationDraftPayload({
            params,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearDonationDraft(draftKey)
            return
        }

        saveDonationDraft(draftKey, nextDraft)
    }, [draftKey, draftReady, hasDetail, params])

    // 상세 저장 후에도 원래 목록 query와 필터를 그대로 유지한 채 복귀한다.
    const moveToList = (snackbarState) => {
        setNavigationUnlocked(true)
        clearDonationDraft(draftKey)

        // 저장 후 이동은 이탈 경고를 잠시 풀고, 목록 query는 그대로 유지한다.
        setTimeout(() => {
            history.push(createListLinkTarget({
                pathname: '/donations',
                search: listSearch,
                curPage: listCurPage,
                searchText: listSearchText,
                searchSelect: listSearchSelect,
                ...(snackbarState || {})
            }))
        }, 0)
    }

    const handleBackClick = (event) => {
        if (!leaveGuardEnabled) {
            clearDonationDraft(draftKey)
            return
        }

        event.preventDefault()

        if (!window.confirm(DONATION_IMAGE_LEAVE_MESSAGE)) {
            return
        }

        moveToList()
    }

    const handleUpdateField = (e) => {
        setParams((prev) => ({
            ...prev,
            [e.target.name]: e.target.value
        }))
    }

    const handleUploadClick = (fieldName) => (e) => {
        e.preventDefault()

        const file = e.target.files?.[0]
        if (!file) {
            return
        }

        if (!isDonationImageFile(file)) {
            e.target.value = ''
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(DONATION_IMAGE_FILE_ERROR_MESSAGE)
            return
        }

        const reader = new FileReader()

        reader.onloadend = () => {
            setParams((prev) => ({
                ...prev,
                [fieldName]: file
            }))
            setPreviewImages((prev) => ({
                ...prev,
                [fieldName]: reader.result
            }))
        }

        reader.readAsDataURL(file)
    }

    const handleClearSelectedImage = (fieldName) => {
        setParams((prev) => ({
            ...prev,
            [fieldName]: originalImageValuesRef.current[fieldName],
        }))
        setPreviewImages((prev) => ({
            ...prev,
            [fieldName]: originalPreviewImagesRef.current[fieldName],
        }))
    }

    const updateData = async (formData) => {
        try {
            setSaving(true)

            const { data } = await API({
                method: 'post',
                url: '/api/v1/admin/donation/update',
                data: formData,
                headers: { 'Content-Type': 'multipart/form-data' }
            })

            if (data.code === 200) {
                moveToList({
                    openSnackbar: true,
                    severitySnackbar: 'success',
                    messageSnackbar: '포인트 기부 상품을 저장했습니다.'
                })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('포인트 기부 상품을 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const trimmedTitle = (params.title || '').trim()
        const trimmedBrandName = (params.brandName || '').trim()
        const normalizedPoint = Number(params.point)
        const hasProductImage = params.productImage instanceof File || Boolean(params.detailImage || params.thumbnailImage)
        const hasLetterBaseImage = params.letterBaseImage instanceof File || Boolean(params.letterBaseImage)

        if (!trimmedTitle) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('상품명을 입력해주세요.')
            return
        }

        if (!trimmedBrandName) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('브랜드명을 입력해주세요.')
            return
        }

        if (!Number.isFinite(normalizedPoint) || normalizedPoint < 1) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('포인트는 1 이상으로 입력해주세요.')
            return
        }

        if (!hasProductImage) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('상품 이미지를 업로드해주세요.')
            return
        }

        if (!hasLetterBaseImage) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('감사 편지 기본 이미지를 업로드해주세요.')
            return
        }

        const formData = new FormData()
        formData.append('donationProductId', donationProductId)
        formData.append('title', trimmedTitle)
        formData.append('brandName', trimmedBrandName)
        formData.append('description', (params.description || '').trim())
        formData.append('point', normalizedPoint)
        formData.append('sortOrder', Number(params.sortOrder || 0))
        formData.append('status', Number(params.status))

        if (params.productImage instanceof File) formData.append('productImage', params.productImage)
        if (params.letterBaseImage instanceof File) formData.append('letterBaseImage', params.letterBaseImage)

        updateData(formData)
    }

    const renderImageField = (fieldName, label, helperText) => (
        <Grid item xs={12} md={4}>
            <DonationImageUploadPanel
                fieldName={fieldName}
                label={label}
                helperText={helperText}
                imageSrc={previewImages[fieldName]}
                imageFile={params[fieldName]}
                disabled={saving}
                onUpload={handleUploadClick}
                onClear={handleClearSelectedImage}
            />
        </Grid>
    )

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="포인트 기부 상품 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" onBackClick={() => moveToList()} backLabel="목록으로" />
            )}
            sx={{ mt: 2 }}
        />
    )

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (!hasDetail) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>포인트 기부 상품 상세</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Prompt when={leaveGuardEnabled} message={DONATION_IMAGE_LEAVE_MESSAGE} />
                <Title>포인트 기부 상품 상세</Title>
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <TextField
                                    label="상품 ID"
                                    name="donationProductId"
                                    value={params.donationProductId || ''}
                                    size="small"
                                    InputProps={{ readOnly: true }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <TextField
                                    label="기부 횟수"
                                    name="exchangeCount"
                                    value={params.exchangeCount || 0}
                                    size="small"
                                    InputProps={{ readOnly: true }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <TextField
                                    label="등록일"
                                    name="regTime"
                                    value={params.regTime || ''}
                                    size="small"
                                    InputProps={{ readOnly: true }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <TextField
                                    label="수정일"
                                    name="updTime"
                                    value={params.updTime || ''}
                                    size="small"
                                    InputProps={{ readOnly: true }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="상품명"
                                    name="title"
                                    value={params.title || ''}
                                    onChange={handleUpdateField}
                                    size="small"
                                    disabled={saving}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="브랜드명"
                                    name="brandName"
                                    value={params.brandName || ''}
                                    onChange={handleUpdateField}
                                    size="small"
                                    disabled={saving}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    type="number"
                                    label="포인트"
                                    name="point"
                                    value={params.point ?? ''}
                                    onChange={handleUpdateField}
                                    size="small"
                                    disabled={saving}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth>
                                <TextField
                                    type="number"
                                    label="정렬순서"
                                    name="sortOrder"
                                    value={params.sortOrder ?? 0}
                                    onChange={handleUpdateField}
                                    size="small"
                                    disabled={saving}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth>
                                <TextField
                                    select
                                    label="상태"
                                    name="status"
                                    value={params.status ?? 1}
                                    onChange={handleUpdateField}
                                    size="small"
                                    disabled={saving}
                                >
                                    <MenuItem value={1}>활성화</MenuItem>
                                    <MenuItem value={0}>비활성화</MenuItem>
                                </TextField>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <TextField
                                    label="설명"
                                    name="description"
                                    value={params.description || ''}
                                    onChange={handleUpdateField}
                                    size="small"
                                    multiline
                                    minRows={4}
                                    disabled={saving}
                                />
                            </FormControl>
                        </Grid>

                        {renderImageField('productImage', '상품 이미지', '원본 이미지 저장 후 서버에서 썸네일을 자동 생성합니다.')}
                        {renderImageField('letterBaseImage', '감사 편지 기본 이미지', '앱에서 문구와 날짜를 오버레이하는 기본 배경 이미지입니다.')}
                    </Grid>

                    <BottomButtonGroup
                        path="/donations"
                        search={listSearch}
                        curPage={listCurPage}
                        searchText={listSearchText}
                        searchSelect={listSearchSelect}
                        visible={true}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                        onBackClick={handleBackClick}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
